import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ThemeMode } from '../lib/theme/themes'
import { profileService } from '@/lib/api/profileService'
import { useFilesStore } from './filesStore'
import {
  applyChatScale,
  applyUiScale,
  normalizeChatScale,
  normalizeUiScale,
  persistChatScale,
  persistUiScale,
  readChatScale,
  readUiScale,
} from '@/lib/uiScale'

export type TransportMode = 'auto' | 'ws' | 'sse'

/**
 * Плотность ленты чата (первая ось режимов отображения).
 *
 * `compact` — у **прошлых** ходов скрыты размышления, карточки инструментов и
 * промежуточные тексты: вместо них одна строка-сводка, раскрываемая по клику.
 * Текущий (живой) ход и ход с ошибкой всегда раскрыты. Режим чисто визуальный:
 * контекст LLM и серверные флаги `hidden`/`disabled` он не трогает.
 */
export type ChatDensity = 'normal' | 'compact'

/**
 * Вид ленты чата (вторая ось режимов отображения, ортогональна `ChatDensity`).
 *
 * `detailed` — текущий вид: у ответа агента пузырь (фон/рамка/скругление/
 * отступы), служебные карточки — карточками. `feed` — ответ агента плоской
 * прозой на фоне чата, без пузыря; служебные строки (размышления, карточки
 * инструментов и саб-агентов) — плоские. Пузырь пользователя остаётся в обоих
 * видах. Режим чисто визуальный и задаётся CSS (классы компонентов не меняются).
 */
export type ChatViewMode = 'detailed' | 'feed'

const LEFT_PANEL_MIN_WIDTH = 280
const LEFT_PANEL_MAX_WIDTH = 500
const RIGHT_PANEL_MIN_WIDTH = 320
const RIGHT_PANEL_MAX_WIDTH = 860

/**
 * Предел числа сохранённых ключей раскрытых сводок компакта: `persist` пишет их
 * в `localStorage`, а лента со временем растёт. Старые ключи вытесняются
 * (хранятся последние `MAX_REVEALED_SERVICE_GROUPS`).
 */
const MAX_REVEALED_SERVICE_GROUPS = 200

interface LeftPanelState {
  isOpen: boolean
  width: number
  minWidth: number
  maxWidth: number
}

interface RightPanelState {
  isOpen: boolean
  width: number
  minWidth: number
  maxWidth: number
}

/** Ключ-обёртка сворачивания блока «Другие проекты» в левой панели (Files/Git). */
export const OTHER_PROJECTS_COLLAPSE_KEY = '__others__'

export type CenterTabType = 'chat' | 'file-viewer' | 'file-editor' | 'commit-detail' | 'pipeline' | 'pipeline-run'

export interface CenterTab {
  id: string
  type: CenterTabType
  title: string
  data: {
    sessionId?: string
    filePath?: string
    projectId?: string
    commitHash?: string
    repoPath?: string
    /** ADR-101: id пайплайна проекта (вкладка «Пайплайн»). */
    pipelineId?: string
    /** ADR-101: id прогона пайплайна (вкладка «Прогон»). */
    runId?: string
  }
  editingState?: 'idle' | 'dirty' | 'saving' | 'saved' | 'error'
}

export type StatusType = 'info' | 'warning' | 'error' | 'success' | 'stream'

export interface StatusMessage {
  id: string
  text: string
  type: StatusType
  timestamp: number
}

export interface ForwardModeState {
  active: boolean
  sourceSessionId: string | null
  sourceIndices: number[]
  mode: 'forward' | 'merge'
}

/** Запрос фокуса на секцию настроек (nonce добавляет стор). */
export interface SettingsFocusRequest {
  categoryId: string
  sectionId?: string
}

/** Ключ persist-хранилища настроек UI (zustand `persist`). */
export const UI_SETTINGS_STORAGE_KEY = 'synth-ui-settings'

/** Поля режима отображения ленты, у которых бывает личный выбор пользователя. */
export type ChatDisplayKey = 'chatViewMode' | 'chatDensity'

/**
 * Личный выбор режима отображения ленты, **зафиксированный до гидратации**
 * стора: `persist` перезаписывает хранилище на каждый `set`, поэтому позже
 * отличить «выбор пользователя» от автоматически записанного дефолта нельзя.
 */
const persistedChatDisplay: Record<ChatDisplayKey, boolean> = readPersistedChatDisplay()
/** Явный выбор в текущей сессии (Save в настройках или значение из профиля). */
const explicitChatDisplay: Record<ChatDisplayKey, boolean> = {
  chatViewMode: false,
  chatDensity: false,
}

function readPersistedChatDisplay(): Record<ChatDisplayKey, boolean> {
  const empty: Record<ChatDisplayKey, boolean> = { chatViewMode: false, chatDensity: false }
  if (typeof window === 'undefined') {
    return empty
  }
  try {
    const raw = window.localStorage.getItem(UI_SETTINGS_STORAGE_KEY)
    if (!raw) {
      return empty
    }
    const parsed = JSON.parse(raw) as { state?: Record<string, unknown> }
    const state = parsed?.state ?? {}
    return {
      chatViewMode: state.chatViewMode !== undefined && state.chatViewMode !== null,
      chatDensity: state.chatDensity !== undefined && state.chatDensity !== null,
    }
  } catch {
    return empty
  }
}

/**
 * Есть ли у пользователя личный выбор режима отображения ленты
 * (сохранённый в `persist` или сделанный/пришедший из профиля в этой сессии).
 * Серверные умолчания применяются только при `false`.
 */
export function hasPersonalChatDisplay(key: ChatDisplayKey): boolean {
  return persistedChatDisplay[key] || explicitChatDisplay[key]
}

/** Черновик расписания для deep-link в вкладку «Шедулер» (ADR-051/ADR-101):
 * предзаполняет форму создания, но ничего не сохраняет. Runtime-only.
 */
export interface ScheduleDraft {
  name: string
  projectId: string
  pipelineId: string
  payload?: Record<string, unknown>
  cron?: string
}

export interface SettingsFocusTarget extends SettingsFocusRequest {
  nonce: number
}

interface UIStore {
  leftPanel: LeftPanelState
  rightPanel: RightPanelState
  toggleLeftPanel: () => void
  setLeftPanelWidth: (width: number) => void
  setLeftPanelOpen: (open: boolean) => void
  toggleRightPanel: () => void
  setRightPanelWidth: (width: number) => void
  setRightPanelOpen: (open: boolean) => void

  /** Мобильные дроверы (phone/tablet) — не персистятся и не связаны с desktop-полями. */
  mobileLeftOpen: boolean
  setMobileLeftOpen: (open: boolean) => void
  mobileRightOpen: boolean
  setMobileRightOpen: (open: boolean) => void

  /** Центральная колонка узкая (десктоп с открытыми панелями) — runtime-флаг,
   *  выставляется ResizeObserver'ом в App. Не персистится. */
  centralNarrow: boolean
  setCentralNarrow: (narrow: boolean) => void

  leftPanelTab: string
  setLeftPanelTab: (tab: string) => void
  rightPanelTab: string
  setRightPanelTab: (tab: string) => void
  collapsedProjects: Set<string>
  toggleProjectCollapse: (projectId: string) => void

  /** Вкладки Files/Git: показывать только проект активного контекста (сессия/файл/коммит). */
  onlyActiveProject: boolean
  setOnlyActiveProject: (value: boolean) => void

  isSettingsDialogOpen: boolean
  setSettingsDialogOpen: (open: boolean) => void
  settingsPage: string
  setSettingsPage: (page: string) => void
  /** Показывать продвинутые категории/секции в настройках (персистится). */
  settingsShowAdvanced: boolean
  setSettingsShowAdvanced: (value: boolean) => void
  /** Одноразовый запрос фокуса на секцию панели (runtime, не персистится). */
  settingsFocus: SettingsFocusTarget | null
  focusSettings: (target: SettingsFocusRequest) => void

  /** Одноразовый черновик расписания для вкладки «Шедулер» (runtime, не персистится). */
  scheduleDraft: ScheduleDraft | null
  setScheduleDraft: (draft: ScheduleDraft | null) => void

  isLogsDialogOpen: boolean
  setLogsDialogOpen: (open: boolean) => void
  logsPage: string
  setLogsPage: (page: string) => void

  /** ADR-102: модальное окно «Аналитика и отчёты» (открытие — меню пользователя / правая панель). */
  isAnalyticsDialogOpen: boolean
  setAnalyticsDialogOpen: (open: boolean) => void

  forwardMode: ForwardModeState
  enterForwardMode: (sourceSessionId: string, sourceIndices: number[], mode: 'forward' | 'merge') => void
  cancelForwardMode: () => void
  forwardConfirmAction: ((targetSessionId: string, position: 'after' | 'before', summarize: boolean) => void) | null

  theme: string
  setTheme: (theme: string) => void
  themeMode: ThemeMode
  setThemeMode: (mode: ThemeMode) => void

  uiScale: number
  setUiScale: (scale: number) => void
  /** Масштаб области чата (множитель поверх `uiScale`); живёт в localStorage. */
  chatScale: number
  setChatScale: (scale: number) => void
  /** Live-preview настроек внешнего вида из панели «Внешний вид» (без записи в профиль). */
  setAppearancePreview: (patch: {
    theme?: string
    themeMode?: ThemeMode
    uiScale?: number
    chatScale?: number
  }) => void

  centerTabs: CenterTab[]
  activeCenterTabId: string | null
  openTab: (tab: CenterTab) => void
  updateTabTitle: (tabId: string, newTitle: string) => void
  setTabEditingState: (tabId: string, editingState: CenterTab['editingState']) => void
  closeTab: (tabId: string) => void
  setActiveCenterTab: (tabId: string) => void
  reorderTabs: (fromIndex: number, toIndex: number) => void
  /** Прозрачно перевести вкладки viewer/editor с путём sourcePath (и потомками) на targetPath. */
  remapFileTabs: (
    sourceProjectId: string,
    sourcePath: string,
    targetProjectId: string,
    targetPath: string,
  ) => void

  transportMode: TransportMode
  setTransportMode: (mode: TransportMode) => void

  intermediateTextDisplay: 'inline' | 'bottom'
  setIntermediateTextDisplay: (mode: 'inline' | 'bottom') => void

  /** Плотность ленты чата (персистится; см. `ChatDensity`). */
  chatDensity: ChatDensity
  setChatDensity: (density: ChatDensity) => void

  /** Вид ленты чата (персистится; см. `ChatViewMode`). */
  chatViewMode: ChatViewMode
  setChatViewMode: (mode: ChatViewMode) => void

  /**
   * Ключ развёрнутой панели ответа (`MessageGroup.key`, `null` — свёрнуто).
   * Персистится: панель переживает перезагрузку и остаётся открытой, пока
   * группа есть в ленте (`pruneChatViewState`).
   */
  expandedChatGroup: string | null
  /** Тумблер разворота: тот же ключ → `null`, другой → новый. */
  toggleChatExpanded: (key: string) => void

  /**
   * Ключи раскрытых сводок скрытых шагов (компактная плотность). Массив, а не
   * `Set`: `persist` сериализует состояние в JSON.
   */
  revealedServiceGroups: string[]
  toggleServiceRevealed: (key: string) => void

  /**
   * Убрать из сохранённого состояния ключи, которых больше нет в ленте
   * (и ограничить размер). Вызывается лентой, когда состав групп изменился.
   */
  pruneChatViewState: (validKeys: string[]) => void

  /**
   * Серверные умолчания вида/плотности ленты инстанса: применяются только если
   * у пользователя нет личного выбора (`hasPersonalChatDisplay`).
   */
  setServerChatDisplayDefaults: (patch: {
    viewMode?: ChatViewMode | null
    density?: ChatDensity | null
  }) => void

  /**
   * Личные значения из профиля (managed): приоритетнее серверных умолчаний,
   * но не перекрывают локальный выбор пользователя.
   */
  applyPersonalChatDisplay: (patch: { viewMode?: ChatViewMode; density?: ChatDensity }) => void

  /** ADR-071 §2.6: сигнал изменения instance-wide `tools.disabled` — статистика
   *  и таблицы MCP перезапрашиваются без рестарта. Runtime, не персистится. */
  toolsDisabledTick: number
  bumpToolsDisabledTick: () => void
}

export const useUIStore = create<UIStore>()(
  persist(
    (set, get) => ({
      leftPanel: {
        isOpen: true,
        width: LEFT_PANEL_MIN_WIDTH,
        minWidth: LEFT_PANEL_MIN_WIDTH,
        maxWidth: LEFT_PANEL_MAX_WIDTH,
      },
      rightPanel: {
        isOpen: false,
        width: RIGHT_PANEL_MIN_WIDTH,
        minWidth: RIGHT_PANEL_MIN_WIDTH,
        maxWidth: RIGHT_PANEL_MAX_WIDTH,
      },

      toggleLeftPanel: () =>
        set((state) => ({
          leftPanel: {
            ...state.leftPanel,
            isOpen: !state.leftPanel.isOpen,
          },
        })),

      setLeftPanelOpen: (open) =>
        set((state) => ({
          leftPanel: {
            ...state.leftPanel,
            isOpen: open,
          },
        })),

      setLeftPanelWidth: (width) =>
        set((state) => ({
          leftPanel: {
            ...state.leftPanel,
            width: Math.min(
              state.leftPanel.maxWidth,
              Math.max(state.leftPanel.minWidth, width)
            ),
          },
        })),

      toggleRightPanel: () =>
        set((state) => ({
          rightPanel: {
            ...state.rightPanel,
            isOpen: !state.rightPanel.isOpen,
          },
        })),

      setRightPanelOpen: (open) =>
        set((state) => ({
          rightPanel: {
            ...state.rightPanel,
            isOpen: open,
          },
        })),

      setRightPanelWidth: (width) =>
        set((state) => ({
          rightPanel: {
            ...state.rightPanel,
            width: Math.min(
              state.rightPanel.maxWidth,
              Math.max(state.rightPanel.minWidth, width)
            ),
          },
        })),

      mobileLeftOpen: false,
      setMobileLeftOpen: (open) => set({ mobileLeftOpen: open }),
      mobileRightOpen: false,
      setMobileRightOpen: (open) => set({ mobileRightOpen: open }),

      centralNarrow: false,
      setCentralNarrow: (narrow) => set({ centralNarrow: narrow }),

      leftPanelTab: 'projects',
      setLeftPanelTab: (tab) => set({ leftPanelTab: tab }),
      // ADR-071: дефолтная вкладка правой панели — «Контекст».
      rightPanelTab: 'context',
      setRightPanelTab: (tab) => set({ rightPanelTab: tab }),
      collapsedProjects: new Set<string>(),
      toggleProjectCollapse: (projectId) =>
        set((state) => {
          const next = new Set(state.collapsedProjects)
          if (next.has(projectId)) {
            next.delete(projectId)
          } else {
            next.add(projectId)
          }
          return { collapsedProjects: next }
        }),

      onlyActiveProject: false,
      setOnlyActiveProject: (value) => set({ onlyActiveProject: value }),

      isSettingsDialogOpen: false,
      setSettingsDialogOpen: (open) => set({ isSettingsDialogOpen: open }),
      settingsPage: 'appearance',
      setSettingsPage: (page) => set({ settingsPage: page }),
      settingsShowAdvanced: false,
      setSettingsShowAdvanced: (value) => set({ settingsShowAdvanced: value }),
      settingsFocus: null,
      focusSettings: (target) =>
        set((state) => ({
          settingsFocus: {
            ...target,
            nonce: (state.settingsFocus?.nonce ?? 0) + 1,
          },
        })),

      scheduleDraft: null,
      setScheduleDraft: (draft) => set({ scheduleDraft: draft }),

      isLogsDialogOpen: false,
      setLogsDialogOpen: (open) => set({ isLogsDialogOpen: open }),
      logsPage: 'core',
      setLogsPage: (page) => set({ logsPage: page }),

      isAnalyticsDialogOpen: false,
      setAnalyticsDialogOpen: (open) => set({ isAnalyticsDialogOpen: open }),

      forwardMode: {
        active: false,
        sourceSessionId: null,
        sourceIndices: [],
        mode: 'forward',
      },
      enterForwardMode: (sourceSessionId, sourceIndices, mode) =>
        set({ forwardMode: { active: true, sourceSessionId, sourceIndices, mode } }),
      cancelForwardMode: () =>
        set({ forwardMode: { active: false, sourceSessionId: null, sourceIndices: [], mode: 'forward' } }),
      forwardConfirmAction: null,

      theme: 'synthesis',
      setTheme: (theme) => {
        set({ theme })
        profileService.syncThemeToProfile(theme)
      },
      themeMode: 'system',
      setThemeMode: (mode) => {
        set({ themeMode: mode })
        profileService.syncThemeModeToProfile(mode)
      },

      uiScale: readUiScale(),
      setUiScale: (scale) => {
        applyUiScale(scale)
        persistUiScale(scale)
        set({ uiScale: normalizeUiScale(scale) })
      },
      chatScale: readChatScale(),
      setChatScale: (scale) => {
        applyChatScale(scale)
        persistChatScale(scale)
        set({ chatScale: normalizeChatScale(scale) })
      },
      setAppearancePreview: (patch) => {
        set(patch)
      },

      centerTabs: [],
      activeCenterTabId: null,

      openTab: (tab) =>
        set((state) => {
          const existingIdx = state.centerTabs.findIndex((t) => t.id === tab.id)
          if (existingIdx >= 0) {
            const nextTabs = [...state.centerTabs]
            nextTabs[existingIdx] = { ...nextTabs[existingIdx], title: tab.title }
            return { centerTabs: nextTabs, activeCenterTabId: tab.id, mobileLeftOpen: false }
          }
          return {
            centerTabs: [...state.centerTabs, tab],
            activeCenterTabId: tab.id,
            mobileLeftOpen: false,
          }
        }),

      updateTabTitle: (tabId, newTitle) =>
        set((state) => ({
          centerTabs: state.centerTabs.map((t) =>
            t.id === tabId ? { ...t, title: newTitle } : t
          ),
        })),

      setTabEditingState: (tabId, editingState) =>
        set((state) => ({
          centerTabs: state.centerTabs.map((t) =>
            t.id === tabId ? { ...t, editingState } : t
          ),
        })),

      closeTab: (tabId) => {
        // Закрываем файловую вкладку — убираем её черновик, чтобы он не «залипал».
        const tab = get().centerTabs.find((t) => t.id === tabId)
        if (
          (tab?.type === 'file-editor' || tab?.type === 'file-viewer') &&
          tab.data.projectId &&
          tab.data.filePath
        ) {
          useFilesStore.getState().clearDraft(tab.data.projectId, tab.data.filePath)
        }
        set((state) => {
          const idx = state.centerTabs.findIndex((t) => t.id === tabId)
          const nextTabs = state.centerTabs.filter((t) => t.id !== tabId)
          let nextActive = state.activeCenterTabId
          if (state.activeCenterTabId === tabId) {
            if (nextTabs.length === 0) {
              nextActive = null
            } else {
              const newIdx = Math.min(idx, nextTabs.length - 1)
              nextActive = nextTabs[newIdx].id
            }
          }
          return { centerTabs: nextTabs, activeCenterTabId: nextActive }
        })
      },

      setActiveCenterTab: (tabId) => set({ activeCenterTabId: tabId }),

      reorderTabs: (fromIndex, toIndex) =>
        set((state) => {
          const tabs = [...state.centerTabs]
          const [moved] = tabs.splice(fromIndex, 1)
          tabs.splice(toIndex, 0, moved!)
          return { centerTabs: tabs }
        }),

      remapFileTabs: (sourceProjectId, sourcePath, targetProjectId, targetPath) =>
        set((state) => {
          // 1. Собрать ремап затронутых вкладок viewer/editor.
          const remapped = new Map<string, CenterTab>()
          const newIds = new Set<string>()
          for (const tab of state.centerTabs) {
            if (tab.type !== 'file-viewer' && tab.type !== 'file-editor') continue
            const { projectId, filePath } = tab.data
            if (projectId !== sourceProjectId || !filePath) continue
            const isSelf = filePath === sourcePath
            const isDescendant = filePath.startsWith(`${sourcePath}/`)
            if (!isSelf && !isDescendant) continue
            const rest = filePath.slice(sourcePath.length) // '' или '/...'
            const newFilePath = `${targetPath}${rest}`
            const prefix = tab.type === 'file-editor' ? 'edit' : 'file'
            const newId = `${prefix}-${targetProjectId}/${newFilePath}`
            if (newId === tab.id && projectId === targetProjectId && filePath === newFilePath) continue
            remapped.set(tab.id, {
              ...tab,
              id: newId,
              title: newFilePath.split('/').pop() ?? newFilePath,
              data: { ...tab.data, projectId: targetProjectId, filePath: newFilePath },
            })
            newIds.add(newId)
          }
          if (remapped.size === 0) return state

          // 2. Чужие (не перемапленные) вкладки, чей id занят перемапленной,
          // удаляем — их файл перезаписан/перенесён; приоритет у перемапленной
          // (сохраняются её editingState и черновик).
          let nextActive = state.activeCenterTabId
          const removedIds = new Set<string>()
          const centerTabs: CenterTab[] = []
          for (const tab of state.centerTabs) {
            const mapped = remapped.get(tab.id)
            if (mapped) {
              centerTabs.push(mapped)
              continue
            }
            if (newIds.has(tab.id)) {
              removedIds.add(tab.id)
              continue
            }
            centerTabs.push(tab)
          }

          // 3. Активная вкладка: перемапленную — перевести на новый id; если
          // активной была удалённая чужая — ею становится перемапленная с тем же id.
          if (state.activeCenterTabId && remapped.has(state.activeCenterTabId)) {
            nextActive = remapped.get(state.activeCenterTabId)!.id
          } else if (nextActive && removedIds.has(nextActive)) {
            for (const mapped of remapped.values()) {
              if (mapped.id === nextActive) {
                nextActive = mapped.id
                break
              }
            }
          }

          if (centerTabs.length === state.centerTabs.length
            && centerTabs.every((tab, i) => tab === state.centerTabs[i])) {
            return state
          }
          return { centerTabs, activeCenterTabId: nextActive }
        }),

      transportMode: 'auto',
      setTransportMode: (mode: TransportMode) => {
        set({ transportMode: mode })
        profileService.syncTransportModeToProfile(mode)
      },

      intermediateTextDisplay: 'inline',
      setIntermediateTextDisplay: (mode) => {
        set({ intermediateTextDisplay: mode })
        profileService.syncIntermediateTextDisplayToProfile(mode)
      },

      chatDensity: 'normal',
      setChatDensity: (density) => {
        explicitChatDisplay.chatDensity = true
        set({ chatDensity: density })
      },

      chatViewMode: 'detailed',
      setChatViewMode: (mode) => {
        explicitChatDisplay.chatViewMode = true
        set({ chatViewMode: mode })
      },

      expandedChatGroup: null,
      toggleChatExpanded: (key) =>
        set((state) => ({
          expandedChatGroup: state.expandedChatGroup === key ? null : key,
        })),

      revealedServiceGroups: [],
      toggleServiceRevealed: (key) =>
        set((state) => {
          if (state.revealedServiceGroups.includes(key)) {
            return { revealedServiceGroups: state.revealedServiceGroups.filter((k) => k !== key) }
          }
          // Храним последние ключи: `localStorage` не растёт вместе с лентой.
          return {
            revealedServiceGroups: [...state.revealedServiceGroups, key].slice(-MAX_REVEALED_SERVICE_GROUPS),
          }
        }),

      pruneChatViewState: (validKeys) => {
        const state = get()
        const valid = new Set(validKeys)
        const revealed = state.revealedServiceGroups.filter((key) => valid.has(key))
        const expanded = state.expandedChatGroup && valid.has(state.expandedChatGroup)
          ? state.expandedChatGroup
          : null
        // Ничего не изменилось — не пишем в стор (и не дёргаем persist/localStorage).
        if (
          expanded === state.expandedChatGroup &&
          revealed.length === state.revealedServiceGroups.length
        ) {
          return
        }
        set({ expandedChatGroup: expanded, revealedServiceGroups: revealed })
      },

      setServerChatDisplayDefaults: ({ viewMode, density }) =>
        set(() => {
          const next: Partial<UIStore> = {}
          if (
            (viewMode === 'feed' || viewMode === 'detailed') &&
            !hasPersonalChatDisplay('chatViewMode')
          ) {
            next.chatViewMode = viewMode
          }
          if (
            (density === 'normal' || density === 'compact') &&
            !hasPersonalChatDisplay('chatDensity')
          ) {
            next.chatDensity = density
          }
          return next
        }),

      applyPersonalChatDisplay: ({ viewMode, density }) =>
        set(() => {
          const next: Partial<UIStore> = {}
          if (viewMode && !hasPersonalChatDisplay('chatViewMode')) {
            next.chatViewMode = viewMode
            // Значение из профиля — тоже личный выбор: умолчания его не перекрывают.
            explicitChatDisplay.chatViewMode = true
          }
          if (density && !hasPersonalChatDisplay('chatDensity')) {
            next.chatDensity = density
            explicitChatDisplay.chatDensity = true
          }
          return next
        }),

      toolsDisabledTick: 0,
      bumpToolsDisabledTick: () => set((state) => ({ toolsDisabledTick: state.toolsDisabledTick + 1 })),
    }),
    {
      name: UI_SETTINGS_STORAGE_KEY,
      partialize: (state) => ({
        leftPanelWidth: state.leftPanel.width,
        rightPanelWidth: state.rightPanel.width,
        leftPanelIsOpen: state.leftPanel.isOpen,
        rightPanelIsOpen: state.rightPanel.isOpen,
        leftPanelTab: state.leftPanelTab,
        rightPanelTab: state.rightPanelTab,
        centerTabs: state.centerTabs,
        activeCenterTabId: state.activeCenterTabId,
        collapsedProjects: [...state.collapsedProjects],
        onlyActiveProject: state.onlyActiveProject,
        settingsShowAdvanced: state.settingsShowAdvanced,
        chatDensity: state.chatDensity,
        chatViewMode: state.chatViewMode,
        expandedChatGroup: state.expandedChatGroup,
        revealedServiceGroups: state.revealedServiceGroups,
      }),
      merge: (persisted, current) => {
        const p = persisted as Record<string, unknown> | undefined
        return {
          ...current,
          leftPanel: {
            ...current.leftPanel,
            width: Math.min(current.leftPanel.maxWidth, Math.max(current.leftPanel.minWidth, (p?.leftPanelWidth as number) ?? current.leftPanel.width)),
            isOpen: (p?.leftPanelIsOpen as boolean) ?? current.leftPanel.isOpen,
          },
          rightPanel: {
            ...current.rightPanel,
            width: Math.min(current.rightPanel.maxWidth, Math.max(current.rightPanel.minWidth, (p?.rightPanelWidth as number) ?? current.rightPanel.width)),
            isOpen: (p?.rightPanelIsOpen as boolean) ?? current.rightPanel.isOpen,
          },
          leftPanelTab: (p?.leftPanelTab as string) ?? current.leftPanelTab,
          rightPanelTab: (p?.rightPanelTab as string) ?? current.rightPanelTab,
          centerTabs: (p?.centerTabs as CenterTab[]) ?? current.centerTabs,
          activeCenterTabId: (p?.activeCenterTabId as string | null) ?? current.activeCenterTabId,
          collapsedProjects: new Set(p?.collapsedProjects as string[] ?? []),
          onlyActiveProject: (p?.onlyActiveProject as boolean) ?? current.onlyActiveProject,
          settingsShowAdvanced:
            (p?.settingsShowAdvanced as boolean) ?? current.settingsShowAdvanced,
          // Неизвестное/отсутствующее значение — `normal` (валидация восстановления).
          chatDensity: p?.chatDensity === 'compact' ? 'compact' : 'normal',
          // `feed` — только явное сохранённое значение, иначе подробный вид.
          chatViewMode: p?.chatViewMode === 'feed' ? 'feed' : 'detailed',
          // Сохранённая развёрнутая панель: только непустая строка.
          expandedChatGroup:
            typeof p?.expandedChatGroup === 'string' && p.expandedChatGroup !== ''
              ? p.expandedChatGroup
              : null,
          // Мусор в массиве отбрасываем; порядок сохраняем, хвост ограничиваем.
          revealedServiceGroups: Array.isArray(p?.revealedServiceGroups)
            ? (p.revealedServiceGroups as unknown[])
                .filter((key): key is string => typeof key === 'string' && key !== '')
                .slice(-MAX_REVEALED_SERVICE_GROUPS)
            : [],
        }
      },
    }
  )
)
